import { LISTA_SITUACAO_SOLICITACAO } from 'src/app/core/models/solicitacao.model';
import { NullSafe } from '../types/null-safe.type';

/**
 * Helper com métodos estáticos para normalização e transformação de strings e valores.
 * Fonte da verdade para pipes de mesmo nome.
 */
export class NormalizerHelper {
  private static readonly moedaNumerica = new Intl.NumberFormat('en-US', {
    useGrouping: false, minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  /** Preço de duas casas antes da multiplicação, compatível com DECIMAL(12,2) do WS. */
  static arredondarMoeda(valor: number): number {
    return Number.isFinite(valor) && valor >= 0 ? Number(this.moedaNumerica.format(valor)) : NaN;
  }

  /** Converte o valor para número ou retorna null se não for convertível. */
  static toNumberOrNull(value: any): number | null {
    return value !== null && value !== undefined && value !== '' && typeof value !== 'boolean' && typeof value !== 'object' ? Number(value) : null;
  }

  /** Formata um valor numérico (string) para moeda BRL (ex: "1500" → "R$ 1.500,00"). */
  static monetaryValue(value?: string | number): string {
    if (value === null || value === undefined || value === '') {
      return '0,00';
    }
    const convert = Number(value.toString().replace(/[^0-9.-]+/g, ''));
    const formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
    return formatter.format(convert);
  }

  /** Formata a entrada de um campo monetário em tempo real e retorna seu valor numérico. */
  static onMonetaryInput(event: any): number {
    let inputValue = event.target.value.replace(/\D/g, ''); // Remove tudo que não é dígito
    const value = inputValue ? parseFloat(inputValue) / 100 : 0;
    event.target.value = value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    return value;
  }

  private static readonly PREPOSITIONS_PT = new Set([
    'de', 'da', 'do', 'das', 'dos', 'em', 'na', 'no', 'nas', 'nos',
    'e', 'com', 'para', 'por', 'ao', 'à', 'a',
  ]);

  /**
   * Capitaliza a primeira letra de cada palavra mantendo preposições em minúsculo
   * (exceto a primeira e última palavra). Espelha NormalizeHelper::normalizeString() do backend.
   */
  static capitalizeWords(value: NullSafe<string>): string {
    if (!value) return '';
    const words = value.toLowerCase().split(' ');
    const lastIndex = words.length - 1;
    return words
      .map((word, index) => {
        if (!word) return word;
        const isEdge = index === 0 || index === lastIndex;
        const isPreposition = NormalizerHelper.PREPOSITIONS_PT.has(word);
        return isEdge || !isPreposition
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : word;
      })
      .join(' ');
  }

  /** Remove zeros à esquerda de uma string numérica (ex: "007" → "7"). */
  static removeZerosAEsquerda(valor: string): string {
    return valor.replace(/^0+/, '');
  }

  /**
   * Normaliza número de projeto ou evento para envio ao backend.
   * Remove zeros à esquerda do número de sequência, preserva `.` e `/`.
   * Exemplos:
   *   '00307/2008'     → '307/2008'
   *   '307.003/2008'   → '307.003/2008'
   *   '00043'          → '43'
   */
  static stripProjetoMask(valor: string): string {
    if (!valor?.trim()) return '';
    const partes = valor.split('/');
    if (partes.length === 2) {
      const seq  = partes[0].replace(/^0+/, '') || '0';
      const year = partes[1];
      return `${seq}/${year}`;
    }
    return valor.replace(/^0+/, '') || valor;
  }


  /** Remove caracteres específicos de uma string. */
  static removeCaracteres(str: string, caracteresParaRemover: string[]): string {
    if (!str || !caracteresParaRemover) return str;
    let regex = new RegExp(`[${caracteresParaRemover.join('')}]`, 'g');
    return str.replace(regex, '');
  }

  /** Remove acentos de uma string (ex: "ação" → "acao"). */
  static removerAcentos(texto: string): string {
    return texto
      .normalize('NFD') // Normaliza a string para separar os acentos
      .replace(/[\u0300-\u036f]/g, ''); // Remove os acentos
  }

  /** Gera um link do WhatsApp a partir de um número de telefone, garantindo o formato correto. */
  static whatsAppLink(telefone: string, mensagem?: string): string {
    if (!telefone) return '#';

    // Remove tudo que não for número
    const numeroLimpo = telefone.replace(/\D/g, '');

    // Garante que tenha DDI 55
    const numeroComDDI = numeroLimpo.startsWith('55') ? numeroLimpo : `55${numeroLimpo}`;

    let link = `https://wa.me/${numeroComDDI}`;
    if (mensagem) {
      link += `?text=${encodeURIComponent(mensagem)}`;
    }
    return link;
  }

  /**
   * Formata uma string de dígitos como CPF ou CNPJ.
   * CPF/CNPJ são SEMPRE strings — nunca numbers. Zeros à esquerda são significativos.
   * Auto-detecta pelo tamanho: ≤ 11 dígitos = CPF (000.000.000-00), caso contrário = CNPJ (00.000.000/0000-00).
   */
  static formatCpfCnpj(value: NullSafe<string>): string {
    if (value === null || value === undefined || value === '') return '';

    // String() como proteção de runtime — o tipo de entrada DEVE ser string.
    // Se chegar number, o zero à esquerda já foi perdido na origem (banco/PDO).
    const digits = String(value).replace(/\D/g, '').substring(0, 14);
    if (!digits) return '';

    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  /**
   * Formata uma data/hora de acordo com máscara específica.
   * Máscaras suportadas: 'dd/MM/yyyy', 'dd/MM/yyyy - 00h00m', 'dd/MM/yyyy - 00:00', 'dd/MM/yyyy - 00h00m00s', 'dd/MM/yyyy - 00:00:00'.
   */
  static dateTime(value: NullSafe<Date | string>, mask: string = 'dd/MM/yyyy - 00:00:00'): string {
    if (!value) return '';

    const date = new Date((value as string)?.replace(' ', 'T'));

    const day = NormalizerHelper.addLeadingZero(date.getDate());
    const month = NormalizerHelper.addLeadingZero(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = NormalizerHelper.addLeadingZero(date.getHours());
    const minutes = NormalizerHelper.addLeadingZero(date.getMinutes());
    const seconds = NormalizerHelper.addLeadingZero(date.getSeconds());

    switch (mask) {
      case 'dd/MM/yyyy':
        return `${day}/${month}/${year}`;
      case 'dd/MM/yyyy - 00h00m':
        return `${day}/${month}/${year} - ${hours}h${minutes}m`;
      case 'dd/MM/yyyy - 00:00':
        return `${day}/${month}/${year} - ${hours}:${minutes}`;
      case 'dd/MM/yyyy - 00h00m00s':
        return `${day}/${month}/${year} - ${hours}h${minutes}m${seconds}s`;
      case 'dd/MM/yyyy - 00:00:00':
      default:
        return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
    }
  }

  /** Preenche o início de um valor com caracteres até atingir o comprimento desejado (ex: padStart(7, 5, '0') → "00007"). */
  static padStart(value: any, targetLength: number, padString: string = '0'): string {
    if (value == null) return '';
    return value.toString().padStart(targetLength, padString);
  }

  /** Retorna o título da situação da solicitação a partir do código. */
  static tituloSituacaoSolicitacao(codSituacao: number): string {
    return LISTA_SITUACAO_SOLICITACAO.find(s => s.CODSITUACAO === codSituacao)?.TITULO || '';
  }

  /** Adiciona zero à esquerda em números de um dígito. */
  private static addLeadingZero(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }
}
